import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useWatch, useFormContext } from 'react-hook-form';

import includes from 'lodash/includes';
import map from 'lodash/map';
import intersection from 'lodash/intersection';

import { StyledSelectArrayInput } from '@src/custom/Styled';

export const SELECT_ALL_VALUE = 'select-all';
export const SELECT_ALL_LABEL = 'Select all';
export const CLEAR_ALL_VALUE = 'clear-all';
export const CLEAR_ALL_LABEL = 'Clear all';

/**
 * A wrapper around SelectArrayInput (via StyledSelectArrayInput) that adds
 * "Select all" and "Clear all" functionality.
 *
 * It uses useWatch and useFormContext instead of useInput to avoid redundant
 * field registrations in React Admin v5, as the underlying SelectArrayInput
 * will call useInput itself.
 */
const SelectAllArrayInput = ({
    source,
    choices,
    optionText = 'name',
    optionValue = 'id',
    groupBy,
    excludeProductLines,
    onChange: onChangeProp,
    ...props
}) => {
    const { setValue } = useFormContext();
    const value = useWatch({ name: source });

    const safeChoices = useMemo(() => choices || [], [choices]);
    const choiceValues = useMemo(() => map(safeChoices, optionValue), [safeChoices, optionValue]);

    const handleOnChange = useCallback((newValueOrEvent) => {
        // Extract value from event if necessary
        const newValue = newValueOrEvent?.target ? newValueOrEvent.target.value : newValueOrEvent;

        // Ensure newValue is an array
        const valueArray = Array.isArray(newValue) ? newValue : [newValue].filter((v) => v !== null && v !== undefined);

        if (includes(valueArray, SELECT_ALL_VALUE)) {
            setValue(source, choiceValues, { shouldDirty: true, shouldValidate: true });
        } else if (includes(valueArray, CLEAR_ALL_VALUE)) {
            setValue(source, [], { shouldDirty: true, shouldValidate: true });
        } else {
            // Normal change - propagate to form
            setValue(source, valueArray, { shouldDirty: true, shouldValidate: true });
            if (onChangeProp) {
                onChangeProp(newValueOrEvent);
            }
        }
    }, [source, choiceValues, onChangeProp, setValue]);

    const isAllSelected = useMemo(() => {
        if (choiceValues.length === 0) return false;

        const safeValue = (value || []).map(String);
        const safeChoiceValues = choiceValues.map(String);

        const currentChoiceValues = intersection(safeValue, safeChoiceValues);
        return currentChoiceValues.length === safeChoiceValues.length;
    }, [value, choiceValues]);

    const choicesWithAll = useMemo(() => {
        if (safeChoices.length === 0) return [];

        return [{
            [optionValue]: isAllSelected ? CLEAR_ALL_VALUE : SELECT_ALL_VALUE,
            [optionText]: isAllSelected ? CLEAR_ALL_LABEL : SELECT_ALL_LABEL,
        }].concat(safeChoices);
    }, [isAllSelected, optionValue, optionText, safeChoices]);

    return (
        <StyledSelectArrayInput
            {...props}
            source={source}
            optionText={optionText}
            optionValue={optionValue}
            choices={choicesWithAll}
            groupBy={groupBy}
            excludeProductLines={excludeProductLines}
            onChange={handleOnChange}
        />
    );
};

SelectAllArrayInput.propTypes = {
    source: PropTypes.string.isRequired,
    choices: PropTypes.array,
    optionText: PropTypes.string,
    optionValue: PropTypes.string,
    onChange: PropTypes.func,
    groupBy: PropTypes.string,
    excludeProductLines: PropTypes.array,
};

export default React.memo(SelectAllArrayInput);
